/**
 * Service-worker update seam (phase C.6).
 *
 * `vite-plugin-pwa` exposes the actual updater through the `virtual:pwa-register`
 * module: `registerSW(...)` returns an `updateSW(reloadPage?)` function that posts
 * `SKIP_WAITING` to the waiting worker and reloads once it takes control. That
 * virtual module only resolves in the real app bundle and pulls the whole PWA
 * runtime in, so it must not be imported from feature code or tests.
 *
 * This module is the indirection: `main.tsx` registers the real `updateSW` via
 * `setServiceWorkerUpdater`, and the rest of the app calls `updateServiceWorker`.
 * When no worker is registered (dev without the SW flag, unsupported browser, or
 * before registration completes) it degrades to a hard reload, which still pulls
 * fresh assets. This replaces the `window.location.reload()` placeholders that
 * C.4/C.5 wired into the update banner and the force-update screen.
 */
export type ServiceWorkerUpdater = (reloadPage?: boolean) => Promise<void> | void;

let updater: ServiceWorkerUpdater | null = null;

/** Registers the real `updateSW` returned by `registerSW`. Pass `null` to detach (tests). */
export const setServiceWorkerUpdater = (next: ServiceWorkerUpdater | null): void => {
  updater = next;
};

/**
 * Applies the waiting service worker and (by default) reloads onto the new build.
 * Falls back to a plain reload when no updater is registered. Never throws — a
 * failed update must not trap the user on a blocking screen with a dead button.
 */
export const updateServiceWorker = async (reloadPage = true): Promise<void> => {
  if (updater) {
    try {
      await updater(reloadPage);
      return;
    } catch {
      // Fall through to the hard reload below if the worker update rejects.
    }
  }

  if (reloadPage) {
    window.location.reload();
  }
};
